/**
 * Persist shopping-session attempt fields across in-tab refresh (personalize + `/checkout`).
 *
 * Pinia has no built-in persistence; this is `$patch` + `$subscribe` + `sessionStorage`
 * (same idea as pinia-plugin-persistedstate, without the dependency). App kill clears
 * `sessionStorage` — cold start must not restore payment-adjacent state.
 *
 * Persists journey + entity IDs + cart / shopping session ids, plus:
 * - ready **addresses** for Recipient / Return cards after `/checkout` reload (TK-12805)
 * - confirmed Stripe `client_secret` so remount can skip a second `confirmPayment` (TK-12803)
 *
 * Cleared by `resetCheckout` (place success, leave checkout-active zone, or new journey/project
 * bind). Totals / shipping / entitlements still rehydrate via prepare / finalize. Card UI
 * completeness flags are not persisted.
 *
 * Checkout URL stays ID-less; journey + entity IDs live in the store.
 *
 * @see `doc/spec/SPEC_shopping_session_lifecycle.md` § Refresh (reload recovery)
 */
import type { Pinia, PiniaPluginContext, StateTree } from 'pinia'
import {
    SHOPPING_SESSION_PERSIST_KEY,
    buildShoppingSessionPersistSnapshot,
    clearShoppingSessionPersist,
    parseShoppingSessionPersistSnapshot,
} from '~~/layers/base/utils/shoppingSessionPersist'

function readSnapshot() {
    if (typeof sessionStorage === 'undefined') {
        return null
    }
    const parsed = parseShoppingSessionPersistSnapshot(
        sessionStorage.getItem(SHOPPING_SESSION_PERSIST_KEY)
    )
    if (!parsed && sessionStorage.getItem(SHOPPING_SESSION_PERSIST_KEY)?.trim()) {
        clearShoppingSessionPersist()
        return null
    }
    return parsed
}

function writeSnapshot(state: StateTree): void {
    if (typeof sessionStorage === 'undefined') {
        return
    }
    const snapshot = buildShoppingSessionPersistSnapshot(state)
    if (!snapshot.journey) {
        clearShoppingSessionPersist()
        return
    }
    sessionStorage.setItem(SHOPPING_SESSION_PERSIST_KEY, JSON.stringify(snapshot))
}

export default defineNuxtPlugin((nuxtApp) => {
    // Avoid `#app` NuxtApp import — IDE/tsconfig often cannot resolve that alias in plugins.
    const $pinia = (nuxtApp as { $pinia?: Pinia }).$pinia
    if (!$pinia) {
        return
    }

    $pinia.use(({ store }: PiniaPluginContext) => {
        if (store.$id !== 'shoppingSession') {
            return
        }

        const saved = readSnapshot()
        if (saved?.journey) {
            const secret = saved.stripeClientSecret?.trim() || null
            // Plugin `store` is StoreGeneric; patch payload is shoppingSession-shaped.
            store.$patch({
                journey: saved.journey,
                designProjectId: saved.designProjectId,
                handwritingProjectId: saved.handwritingProjectId,
                handwritingStationeryId: saved.handwritingStationeryId,
                shoppingSessionId: saved.shoppingSessionId,
                ...(secret ? { stripeClientSecret: secret } : {}),
                ...(saved.cartId
                    ? {
                          cart: {
                              status: 'ready',
                              data: { cartId: saved.cartId },
                              skeleton: { cartId: null },
                              error: undefined,
                          },
                      }
                    : {}),
                ...(saved.addresses?.shippingAddress || saved.addresses?.billingAddress
                    ? {
                          addresses: {
                              status: 'ready',
                              data: {
                                  shippingAddress: saved.addresses.shippingAddress ?? null,
                                  billingAddress: saved.addresses.billingAddress ?? null,
                              },
                              skeleton: {
                                  shippingAddress: null,
                                  billingAddress: null,
                              },
                              error: undefined,
                          },
                      }
                    : {}),
            } as StateTree)
        }

        store.$subscribe((_mutation, state) => {
            writeSnapshot(state)
        })
    })
})
